import { useState, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

import AppModal from "@shared/ui/Modal";
import Button from "@shared/ui/Button";
import { Input } from "@shared/ui/Inputs";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";

import { useForgotPassword } from "./hooks";

const emailPattern =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onFooterLinkClick?: () => void;
}

export const ForgotPasswordModal = ({
  isOpen,
  onClose,
  onFooterLinkClick,
}: ForgotPasswordModalProps) => {
  const { messages: msg } = useIntl();
  const [loading, setLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const { submitForgotPassword } = useForgotPassword();

  const {
    register,
    handleSubmit,
    clearErrors,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (isOpen) {
      reset();
      setResetEmailSent(false);
      setLoading(false);
    }
  }, [isOpen, reset]);

  const onSubmit = async (data) => {
    setLoading(true);

    submitForgotPassword(data.email, {
      onSuccess: () => {
        setResetEmailSent(true);
        setLoading(false);
      },
      onError: (e) => {
        setLoading(false);
        toast(
          <Toast
            message={
              e?.message || `${msg.common["errors"]["somethingWentWrong"]}`
            }
            type={TOAST_TYPES.ERROR}
          />,
          { closeButton: false },
        );
      },
    });
  };

  const onError = () => {
    setLoading(false);
  };

  const emailField = register("email", {
    required: {
      value: true,
      message: msg?.common["errors"]["emailRequired"],
    },
    pattern: {
      value: emailPattern,
      message: msg?.login["email"]["invalidEmail"],
    },
  });

  const resetForm = useCallback(() => {
    clearErrors("email");
    setResetEmailSent(false);
  }, [clearErrors]);

  if (resetEmailSent) {
    return (
      <AppModal
        isOpen={isOpen}
        title={msg?.login["forgotPassword"]["resetSuccess"]["title"]}
        handleClose={
          loading
            ? undefined
            : () => {
                resetForm();
                onClose?.();
              }
        }
        maxWidth="!max-w-[400px]"
        content={
          <div className="flex flex-col gap-lg">
            <BodyText
              size={BODY_TEXT_SIZES.SMALL}
              color="text-secondary"
              className="mb-2"
            >
              {msg?.login["forgotPassword"]["resetSuccess"]["message"]}
            </BodyText>
            <Input
              keyId="email-address"
              label={msg.login["forgotPassword"]["email"]["label"]}
              type="email"
              disabled
              value={watch("email")}
              customInputClasses="bg-accent border-transparent"
            />
          </div>
        }
        footer={
          <div className="px-6 pb-6">
            <Button
              fullWidth={true}
              text={msg.common["signUp"]["signInButton"]}
              onClick={onFooterLinkClick}
              className="mb-4"
            />
            <div className="flex justify-center">
              <BodyText color="text-secondary">
                {msg.customerPortals["welcome"]["rememberPassword"]}
              </BodyText>{" "}
              <BodyText
                onClick={onFooterLinkClick}
                color="text-brand"
                className="!font-bold ml-1 cursor-pointer"
              >
                {msg.customerPortals["welcome"]["login"]}
              </BodyText>
            </div>
          </div>
        }
      />
    );
  }

  return (
    <AppModal
      isOpen={isOpen}
      title={msg.login["forgotPassword"]["title"]}
      handleClose={
        loading
          ? undefined
          : () => {
              resetForm();
              onClose?.();
            }
      }
      maxWidth="!max-w-[400px]"
      content={
        <form
          onSubmit={handleSubmit(onSubmit, onError)}
          className="flex flex-col gap-lg"
        >
          <BodyText
            size={BODY_TEXT_SIZES.SMALL}
            color="text-secondary"
            className="mb-2"
          >
            {msg.login["forgotPassword"]["message"]}
          </BodyText>
          <Input
            keyId="email"
            label={msg.login["forgotPassword"]["email"]["label"]}
            name="email"
            type="email"
            placeholder={msg.login["forgotPassword"]["email"]["placeholder"]}
            {...emailField}
            setRef={emailField.ref}
            errorMessage={errors?.email?.message?.toString()}
            customInputClasses={
              errors?.email?.message
                ? "bg-primary border-danger focus:bg-primary focus:border-danger"
                : watch("email")
                ? "bg-primary border-primary"
                : "bg-accent border-transparent"
            }
          />
        </form>
      }
      footer={
        <div className="px-6 pb-6">
          <Button
            fullWidth={true}
            text={msg.login["forgotPassword"]["reset"]}
            className="mb-4"
            disabled={loading}
            onClick={handleSubmit(onSubmit, onError)}
          />
          <div className="flex justify-center">
            <BodyText color="text-secondary">
              {msg.customerPortals["welcome"]["rememberPassword"]}
            </BodyText>{" "}
            <BodyText
              onClick={onFooterLinkClick}
              color="text-brand"
              className="!font-bold ml-1 cursor-pointer"
            >
              {msg.customerPortals["welcome"]["login"]}
            </BodyText>
          </div>
        </div>
      }
    />
  );
};
